import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { Error, Label } from '@progress/kendo-react-labels';
import { toDataSourceRequestString } from '@progress/kendo-data-query';
import { TextArea, NumericTextBox, Input } from '@progress/kendo-react-inputs';

import {
  formatSubDate,
  getStatusBadge
} from '../../components/activity-event/wst';
import {
  useGetPlaylistChallengeSubmissionsById,
  useGetSubmissionsApprovalQueue
} from '../../hooks/services/use-challenges';
import { useToast } from '../../contexts/toast-context';
import { PrivatePage } from '../../components/private-page';
import DurationPicker from '../../components/forms/duration-picker';
import { ApprovePlaylistChallengeSubmission } from '../../services/events';
import { NoteInput } from '../../components/activity-event/wst/note-input';

const InfoText: React.FC<{ heading: string; text: string }> = ({
  text,
  heading
}) => {
  return (
    <div className={'d-flex flex-column'}>
      <span
        style={{
          color: '#A5B7BF'
        }}>
        {heading}
      </span>
      <span className={'fw-bold'}>{text}</span>
    </div>
  );
};

export const WSTApprovalDetailView: React.FC = () => {
  const { eventId, playlistId, id } = useParams();
  const navigate = useNavigate();

  const [score, setScore] = React.useState<number>(0);
  const [timeValue, setTimeValue] = React.useState<number>(0);
  const [notes, setNotes] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [eventTitle, setEventTitle] = React.useState<string>('');
  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetPlaylistChallengeSubmissionsById(
    eventId,
    playlistId,
    id
  );

  const { data: queue } = useGetSubmissionsApprovalQueue(
    toDataSourceRequestString({
      sort: [{ field: 'submittedDateUtc', dir: 'desc' }]
    })
  );

  const statusInfo = getStatusBadge(data?.status);

  const challengeIndex = React.useMemo(() => {
    if (!id) return -1;
    return queue?.data.findIndex((q) => q.id === parseInt(id));
  }, [id, queue]);

  const handlePrevNextButton = React.useCallback(
    (action: 'prev' | 'next') => {
      if (challengeIndex === undefined) return;
      const destinationChallenge = queue?.data.at(
        action === 'prev' ? challengeIndex - 1 : challengeIndex + 1
      );

      if (!destinationChallenge) return;
      navigate(
        `/administration/wst/pending/${destinationChallenge.eventId}/${destinationChallenge.eventPlaylistId}/${destinationChallenge.id}`
      );
    },
    [challengeIndex, queue?.data]
  );

  const handleReviewAction = async (action: 'Approved' | 'Rejected') => {
    if (!eventId || !playlistId || !id) return;
    setIsSubmitting(action === 'Approved' ? 'Approving' : 'Declining');
    setSubmitError(null);
    try {
      const finalScore = data?.isTimeBased ? timeValue : score;
      await ApprovePlaylistChallengeSubmission(
        parseInt(eventId),
        parseInt(playlistId),
        parseInt(id),
        {
          action,
          adjusted: finalScore,
          internalNotes: notes,
          eventName: eventTitle
        }
      );

      onToastOpen({
        style: 'success',
        title: 'WST Submission Review',
        body: (
          <div>
            Challenge submission has been {action.toLowerCase()} successfully.
          </div>
        )
      });

      if (action !== 'Approved') {
        setScore(0);
        setTimeValue(0);
      }
      mutate();
    } catch (err: any) {
      console.error(err);
      setSubmitError(
        err?.message || 'Failed to submit review action. Please try again.'
      );
    } finally {
      setIsSubmitting(null);
    }
  };

  React.useEffect(() => {
    if (!data) return;

    const notes = data.approvalNotes ?? '';
    const isOfficialScore = data.officialScore && data.officialScore > 0;
    setNotes(notes);
    setEventTitle(data.eventTitle);
    data.isTimeBased
      ? setTimeValue(
          isOfficialScore ? data.officialScore : (data.completionTime ?? 0)
        )
      : setScore(isOfficialScore ? data.officialScore : (data.number ?? 0));
  }, [data]);

  return (
    <PrivatePage
      breadcrumb={[
        { label: 'Dashboard', href: 'dashboard' },
        { label: 'Administration', href: '../administration' },
        {
          label: 'WST Challenge Pending Approval',
          href: 'administration/wst/pending'
        },
        {
          label: 'WST Challenge Pending Approval Details',
          href: 'administration/wst/pending/' + id
        }
      ]}
      pageTitle={'Administration'}>
      {isLoading && (
        <div
          className={
            'd-flex align-items-center justify-content-center h-100 w-100'
          }>
          <Loader type={'infinite-spinner'} size={'large'} />
        </div>
      )}

      <div className={`d-flex flex-column gap-3 ${isLoading ? 'd-none' : ''}`}>
        <div className={'d-flex justify-content-between align-items-center'}>
          <span className={'fw-bold'}>Review submission</span>

          <div className={'d-flex gap-2'}>
            <Button
              fillMode={'outline'}
              themeColor={'secondary'}
              disabled={challengeIndex === 0}
              onClick={() => handlePrevNextButton('prev')}>
              Previous
            </Button>
            <Button
              themeColor={'success'}
              disabled={challengeIndex === (queue?.total ?? 0) - 1}
              onClick={() => handlePrevNextButton('next')}>
              Next
            </Button>
          </div>
        </div>

        <div className={'card p-4 gap-2'}>
          <span className={'fw-bold'}>Challenge Details</span>
          <div
            className={'d-flex'}
            style={{
              gap: '92px'
            }}>
            <InfoText heading={'Game'} text={data?.challengeName ?? ''} />
            <InfoText
              heading={'Event date'}
              text={formatSubDate(data?.eventDate)}
            />
            <InfoText
              heading={'Location'}
              text={`${data?.city ?? ''}${data?.city ? ',' : ''} ${data?.location ?? ''}`}
            />
            <InfoText heading={'Pax'} text={data?.pax ?? ''} />
          </div>
        </div>

        <div className={'card p-4 gap-2'}>
          <div className={'d-flex justify-content-between align-items-center'}>
            <span className={'fw-bold'}>Submission</span>

            <span
              style={{
                padding: '4px 8px',
                borderRadius: '5px',
                fontSize: '14px',
                ...statusInfo.style
              }}>
              {statusInfo.text}
            </span>
          </div>

          <Row className={'m-0'}>
            <Col className={'pl-0 d-flex flex-column gap-2'}>
              <div
                className={'d-flex'}
                style={{
                  gap: '92px'
                }}>
                <InfoText
                  heading={'Submitted on'}
                  text={formatSubDate(data?.submittedDateUtc)}
                />
                <InfoText
                  heading={'Submitted by'}
                  text={data?.submittedByName ?? ''}
                />
                <InfoText
                  heading={'Organisation'}
                  text={data?.organisation ?? ''}
                />
              </div>

              <video
                src={data?.videoUrl ?? ''}
                height={350}
                width={'100%'}
                controls
              />
              <Col md={12} className={'mb-3'}>
                <Label
                  style={{
                    fontSize: '12px'
                  }}>
                  Attempt notes
                </Label>
                <TextArea
                  value={data?.submissionNotes}
                  rows={3}
                  readOnly
                  style={{
                    backgroundColor: '#e9ecef',
                    color: '#6c757d',
                    cursor: 'default'
                  }}
                />
              </Col>
            </Col>

            <Col className={'pr-0 d-flex flex-column flex-grow-1'}>
              <div
                className={'card p-4 d-flex flex-column gap-2 h-100'}
                style={{
                  backgroundColor: '#F8FCFE'
                }}>
                <span className={'fw-bold'}>Admin</span>
                <div className={'d-flex flex-column w-100'}>
                  <Label style={{ fontSize: '12px' }}>Event title</Label>
                  <div className={'d-flex gap-2 align-items-center'}>
                    <Input
                      style={{
                        width: '100%'
                      }}
                      value={eventTitle}
                      onChange={(e) => setEventTitle(e.value)}
                    />

                    {eventTitle !== data?.eventTitle && (
                      <span
                        className={
                          'material-symbols-outlined text-primary cursor-pointer text-decoration-none'
                        }
                        onClick={() => {
                          setEventTitle(data?.eventTitle ?? '');
                        }}
                        title={'Reset to default'}>
                        restart_alt
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  {data?.isTimeBased ? (
                    <>
                      <Label style={{ fontSize: '12px' }}>Result</Label>
                      <DurationPicker
                        value={timeValue ?? 0}
                        onChange={(e) => setTimeValue(e)}
                        disabled={isSubmitting !== null}
                        className={'mr-2'}
                        format="mm:ss"
                        nowButton={false}
                      />
                    </>
                  ) : (
                    <>
                      <Label style={{ fontSize: '12px' }}>Result</Label>
                      <NumericTextBox
                        value={score}
                        onChange={(e) => setScore(e.value || 0)}
                        min={0}
                        format="d"
                        disabled={isSubmitting !== null}
                      />
                    </>
                  )}
                </div>
                <div className={'d-flex flex-column flex-grow-1'}>
                  <NoteInput
                    label={'Feedback'}
                    value={notes}
                    placeholder={
                      'Provide feedback for the submitting team. This feedback will be included in the submission notification.'
                    }
                    handleInputChange={(value) => setNotes(value)}
                    isDisabled={isSubmitting !== null}
                  />
                </div>
                <div className={'d-flex flex-column gap-2'}>
                  <Button
                    className={`${data?.status.toLowerCase() === 'rejected' ? 'd-none' : ''}`}
                    fillMode={'outline'}
                    themeColor={'error'}
                    disabled={isSubmitting !== null}
                    onClick={() => handleReviewAction('Rejected')}>
                    {isSubmitting === 'Declining'
                      ? `${isSubmitting}...`
                      : 'Decline'}
                  </Button>
                  <Button
                    className={`${data?.status.toLowerCase() === 'approved' ? 'd-none' : ''}`}
                    themeColor={'success'}
                    disabled={isSubmitting !== null}
                    onClick={() => handleReviewAction('Approved')}>
                    {isSubmitting === 'Approving'
                      ? `${isSubmitting}...`
                      : 'Approve'}
                  </Button>
                </div>

                {submitError && <Error>{submitError}</Error>}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </PrivatePage>
  );
};
